import type { SyntheticEvent } from "react";
import Box from "@mui/material/Box";
import { LocalizationProvider } from '@mui/x-date-pickers-v.6/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers-v.6/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers-v.6/DatePicker';
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { BooleanSwitchValue } from "consts/general";
import { getFormInputProps } from "util/forms";
import { InputableRadioGroup, OnboardingInputControl } from "ui/molecules/Input";
import OnboardingForm from "ui/forms/ECommerce/Onboarding/OnboardingForm";
import { CountryIso2 } from "ui/widgets/CountrySelector";
import StatefulSelector from "ui/widgets/StatefulSelector";
import { FormField, type OnboardingBusinessInformationProps } from "./types";
import { selectOnboardingLegalForms } from "features/general/selectors";
import UseCompanyContactDetails from "./UseCompanyContactDetails";
import UseSinceIncorporationDate from "./UseSinceIncorporationDate";

const BusinessInformation = ({
    values,
    isSubmitting,
    isValid,
    isBootstrapDataLoading,
    isIncomplete,
    requestedChangeFields,
    bootstrapData,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
    handleSubmit,
    ...form
}: OnboardingBusinessInformationProps) => {
    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    return (
        <OnboardingForm
            {...form}
            isSubmitting={isSubmitting}
            isValid={isValid}
            isIncomplete={isIncomplete}
            requestedChangeFields={requestedChangeFields}
            handleSubmit={handleSubmit}
        >
            <Typography
                variant='h6'
                gutterBottom
            >
                Company Details
            </Typography>
            <OnboardingInputControl
                name={FormField.LegalNameOfBusiness}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.LegalNameOfBusiness, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.LegalNameOfBusiness}
                        value={values[FormField.LegalNameOfBusiness]}
                        label='Legal Name of Business (of Incorporated Entity)'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.TradeName}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.TradeName, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.TradeName}
                        value={values[FormField.TradeName]}
                        label='DBA/Trade Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.EntityType}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <StatefulSelector
                        fullWidth
                        disableClearable
                        disabled={disabled}
                        layout={null}
                        selectOptions={selectOnboardingLegalForms}
                        value={values[FormField.EntityType]}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.EntityType, form),
                            onBlur: handleBlur,
                            required: true,
                            fullWidth: true,
                            name: FormField.EntityType,
                            label: 'Entity Type'
                        }}
                        onChange={onAutocompleteChange(FormField.EntityType)}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Incorporation Details
            </Typography>
            <OnboardingInputControl
                name={FormField.IncorporationId}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.IncorporationId, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.IncorporationId}
                        value={values[FormField.IncorporationId]}
                        label='Incorporation ID#'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.CountryOfIncorporation}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <CountryIso2
                        fullWidth
                        disableClearable
                        FormControlProps={{
                            sx: {
                                width: '100%'
                            }
                        }}
                        disabled={disabled}
                        value={values[FormField.CountryOfIncorporation] ?? ''}
                        onChange={onAutocompleteChange(FormField.CountryOfIncorporation)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.CountryOfIncorporation, form),
                            required: true,
                            name: FormField.CountryOfIncorporation,
                            label: 'Incorporation Country'
                        }}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.IncorporationDate}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <LocalizationProvider
                        dateAdapter={AdapterMoment}
                    >
                        <DatePicker
                            disableFuture
                            label='Incorporation Date'
                            views={['year', 'month', 'day']}
                            disabled={disabled}
                            slotProps={{
                                textField: {
                                    ...getFormInputProps(FormField.IncorporationDate, form),
                                    name: FormField.IncorporationDate,
                                    size: 'small',
                                    required: true,
                                    fullWidth: true,
                                    onBlur: handleBlur
                                }
                            }}
                            value={values[FormField.IncorporationDate]}
                            onChange={value => {
                                setFieldValue(FormField.IncorporationDate, value);
                                setTimeout(() => setFieldTouched(FormField.IncorporationDate, true, true));
                            }}
                        />
                    </LocalizationProvider>
                )}
            />
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    alignItems: 'center',
                    gap: 1,
                    mt: 2
                }}
            >
                <OnboardingInputControl
                    name={FormField.YearsUnderOwnership}
                    isIncomplete={isIncomplete}
                    requestedChangeFields={requestedChangeFields}
                    renderInput={({ disabled }) => (
                        <TextField
                            {...getFormInputProps(FormField.YearsUnderOwnership, form)}
                            fullWidth
                            type='number'
                            disabled={disabled || values[FormField.UseSinceIncorporationDate]}
                            size='small'
                            name={FormField.YearsUnderOwnership}
                            value={values[FormField.YearsUnderOwnership]}
                            label='Years under current ownership'
                            onChange={handleChange}
                            onBlur={handleBlur}
                        />
                    )}
                />
                <UseSinceIncorporationDate
                    {...form}
                    name={FormField.UseSinceIncorporationDate}
                    values={values}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    handleBlur={handleBlur}
                    targetField={FormField.YearsUnderOwnership}
                    sourceField={FormField.IncorporationDate}
                />
            </Box>
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Company Address
            </Typography>
            <OnboardingInputControl
                name={FormField.CompanyAddress}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.CompanyAddress, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.CompanyAddress}
                        value={values[FormField.CompanyAddress]}
                        label='Location Address'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.MailingAddress}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.MailingAddress, form)}
                        fullWidth
                        disabled={disabled}
                        size='small'
                        name={FormField.MailingAddress}
                        value={values[FormField.MailingAddress]}
                        label='Mailing Address (Optional)'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.Country}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <CountryIso2
                        fullWidth
                        disableClearable
                        FormControlProps={{
                            sx: {
                                width: '100%'
                            }
                        }}
                        disabled={disabled}
                        value={values[FormField.Country] ?? ''}
                        onChange={onAutocompleteChange(FormField.Country)}
                        TextFieldProps={{
                            ...getFormInputProps(FormField.Country, form),
                            required: true,
                            name: FormField.Country,
                            label: 'Country'
                        }}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.City}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.City, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.City}
                        value={values[FormField.City]}
                        label='City'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.PostalCode}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.PostalCode, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.PostalCode}
                        value={values[FormField.PostalCode]}
                        label='Postal Code'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Company Contact
            </Typography>
            <OnboardingInputControl
                name={FormField.ContactName}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.ContactName, form)}
                        fullWidth
                        required
                        disabled={disabled}
                        size='small'
                        name={FormField.ContactName}
                        value={values[FormField.ContactName]}
                        label='Contact Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.ContactPhone}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.ContactPhone, form)}
                        fullWidth
                        required
                        type='tel'
                        disabled={disabled}
                        size='small'
                        name={FormField.ContactPhone}
                        value={values[FormField.ContactPhone]}
                        label='Business Phone'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.ContactEmail}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.ContactEmail, form)}
                        fullWidth
                        required
                        type='email'
                        disabled={disabled}
                        size='small'
                        name={FormField.ContactEmail}
                        value={values[FormField.ContactEmail]}
                        label='Business Email'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Technical Contact
            </Typography>
            <UseCompanyContactDetails
                {...form}
                name={FormField.UseContactDeatilsForTech}
                values={values}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                handleBlur={handleBlur}
                targetFields={[
                    FormField.TechContactName,
                    FormField.TechContactPhone,
                    FormField.TechContactEmail
                ]}
            />
            <OnboardingInputControl
                name={FormField.TechContactName}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.TechContactName, form)}
                        fullWidth
                        required
                        disabled={disabled || values[FormField.UseContactDeatilsForTech]}
                        size='small'
                        name={FormField.TechContactName}
                        value={values[FormField.TechContactName]}
                        label='Contact Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.TechContactPhone}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.TechContactPhone, form)}
                        fullWidth
                        required
                        type='tel'
                        disabled={disabled || values[FormField.UseContactDeatilsForTech]}
                        size='small'
                        name={FormField.TechContactPhone}
                        value={values[FormField.TechContactPhone]}
                        label='Business Phone'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.TechContactEmail}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.TechContactEmail, form)}
                        fullWidth
                        required
                        type='email'
                        disabled={disabled || values[FormField.UseContactDeatilsForTech]}
                        size='small'
                        name={FormField.TechContactEmail}
                        value={values[FormField.TechContactEmail]}
                        label='Business Email'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Financial Contact
            </Typography>
            <UseCompanyContactDetails
                 {...form}
                name={FormField.UseContactDeatilsForFin}
                values={values}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                handleBlur={handleBlur}
                targetFields={[
                    FormField.FinContactName,
                    FormField.FinContactPhone,
                    FormField.FinContactEmail
                ]}
            />
            <OnboardingInputControl
                name={FormField.FinContactName}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.FinContactName, form)}
                        fullWidth
                        required
                        disabled={disabled || values[FormField.UseContactDeatilsForFin]}
                        size='small'
                        name={FormField.FinContactName}
                        value={values[FormField.FinContactName]}
                        label='Contact Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.FinContactPhone}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.FinContactPhone, form)}
                        fullWidth
                        required
                        type='tel'
                        disabled={disabled || values[FormField.UseContactDeatilsForFin]}
                        size='small'
                        name={FormField.FinContactPhone}
                        value={values[FormField.FinContactPhone]}
                        label='Business Phone'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.FinContactEmail}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.FinContactEmail, form)}
                        fullWidth
                        required
                        type='email'
                        disabled={disabled || values[FormField.UseContactDeatilsForFin]}
                        size='small'
                        name={FormField.FinContactEmail}
                        value={values[FormField.FinContactEmail]}
                        label='Business Email'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Emergency Contact
            </Typography>
            <UseCompanyContactDetails
                {...form}
                name={FormField.UseContactDeatilsForEmergency}
                values={values}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                handleBlur={handleBlur}
                targetFields={[
                    FormField.EmergencyContactName,
                    FormField.EmergencyContactPhone,
                    FormField.EmergencyContactEmail
                ]}
            />
            <OnboardingInputControl
                name={FormField.EmergencyContactName}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.EmergencyContactName, form)}
                        fullWidth
                        required
                        disabled={disabled || values[FormField.UseContactDeatilsForEmergency]}
                        size='small'
                        name={FormField.EmergencyContactName}
                        value={values[FormField.EmergencyContactName]}
                        label='Contact Name'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.EmergencyContactPhone}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.EmergencyContactPhone, form)}
                        fullWidth
                        required
                        type='tel'
                        disabled={disabled || values[FormField.UseContactDeatilsForEmergency]}
                        size='small'
                        name={FormField.EmergencyContactPhone}
                        value={values[FormField.EmergencyContactPhone]}
                        label='Business Phone'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <OnboardingInputControl
                name={FormField.EmergencyContactEmail}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.EmergencyContactEmail, form)}
                        fullWidth
                        required
                        type='email'
                        disabled={disabled || values[FormField.UseContactDeatilsForEmergency]}
                        size='small'
                        name={FormField.EmergencyContactEmail}
                        value={values[FormField.EmergencyContactEmail]}
                        label='Business Email'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='h6'
                gutterBottom
            >
                Integration
            </Typography>
            <OnboardingInputControl
                name={FormField.WishDirectIntegration}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <InputableRadioGroup
                        textInputRequiredWhen={BooleanSwitchValue.Off}
                        slotProps={{
                            typography: {
                                children: 'Do you wish to integrate with us directly?'
                            },
                            formControlLabel: {
                                disabled
                            },
                            radioGroup: {
                                'aria-labelledby': FormField.WishDirectIntegration,
                                name: FormField.WishDirectIntegration,
                                value: values[FormField.WishDirectIntegration],
                                onChange: handleChange,
                                onBlur: handleBlur
                            },
                            textField: {
                                ...getFormInputProps(FormField.WishDirectIntegrationInput, form),
                                disabled,
                                name: FormField.WishDirectIntegrationInput,
                                value: values[FormField.WishDirectIntegrationInput],
                                label: 'IF NO: Which Checkout/Cashier will you be using?',
                                onChange: handleChange,
                                onBlur: handleBlur
                            }
                        }}
                    />
                )}
            />
            <Typography
                marginTop={2}
                variant='subtitle1'
                gutterBottom
            >
                Please provide a Google email address to be used for integration and implementation (Google Chat)
            </Typography>
            <OnboardingInputControl
                name={FormField.IntegrationContactEmail}
                isIncomplete={isIncomplete}
                requestedChangeFields={requestedChangeFields}
                renderInput={({ disabled }) => (
                    <TextField
                        {...getFormInputProps(FormField.IntegrationContactEmail, form)}
                        fullWidth
                        required
                        type='email'
                        disabled={disabled}
                        size='small'
                        name={FormField.IntegrationContactEmail}
                        value={values[FormField.IntegrationContactEmail]}
                        label='Email'
                        onChange={handleChange}
                        onBlur={handleBlur}
                    />
                )}
            />
        </OnboardingForm>
    );
};

export default BusinessInformation;
